import React, { useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import OsScroll from './OsScroll';
import { EMOJI_CATEGORIES, EMOJIS_BY_CATEGORY, searchEmojis } from './emojiData';

/**
 * Our own emoji picker, replacing emoji-picker-react's component.
 *
 * WHY IT IS OURS. The third-party picker drew the platform's native scrollbar,
 * and there was no way to fix that from outside it. CSS cannot: macOS WKWebView
 * ignores ::-webkit-scrollbar whenever the system uses overlay scrollbars (the
 * reason OsScroll exists at all, documented in OsScroll.js). Attaching
 * OverlayScrollbars to its internal `.epr-body` did work on the scrollbar and
 * broke the picker's layout, because that element is the one the library sizes
 * itself from. Two failed attempts at bending someone else's component; the
 * owner asked for a custom one instead, which is the right call — a component
 * we render is a component OsScroll can simply wrap.
 *
 * Only the COMPONENT is ours. The data still comes from that package (see
 * emojiData.js) — no reason to ship a second copy of every emoji.
 *
 * One category is rendered at a time rather than all 1898 emoji at once. That
 * is not premature: the full set is ~1900 buttons, and mounting them on every
 * open is what makes a picker feel sluggish. Search escapes the category and
 * looks across all of them.
 */
export default function EmojiGridPicker({ onSelect, autoFocus = true }) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState(EMOJI_CATEGORIES[0].id);
  const inputRef = useRef(null);

  const searching = query.trim() !== '';
  const results = useMemo(() => (searching ? searchEmojis(query) : []), [query, searching]);
  const shown = searching ? results : EMOJIS_BY_CATEGORY[category] || [];

  return (
    <div className="emoji-grid">
      <div className="emoji-grid-search">
        <Search size={14} className="emoji-grid-search-icon" aria-hidden />
        <input
          ref={inputRef}
          type="text"
          className="emoji-grid-search-input"
          placeholder="Search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          spellCheck={false}
          autoComplete="off"
          aria-label="Search emoji"
        />
      </div>

      {/* Hidden while searching: the tabs would be lying, since results span
          every category and clicking one would silently discard the query. */}
      {!searching && (
        <div className="emoji-grid-tabs" role="tablist" aria-label="Emoji categories">
          {EMOJI_CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={c.id === category}
              aria-label={c.label}
              title={c.label}
              className={`emoji-grid-tab${c.id === category ? ' is-active' : ''}`}
              onClick={() => setCategory(c.id)}
            >
              <span aria-hidden>{c.icon}</span>
            </button>
          ))}
        </div>
      )}

      {/* OsScroll, not overflow:auto — the whole point of this component. */}
      <OsScroll className="emoji-grid-scroll">
        <div className="emoji-grid-label">
          {searching
            ? `${results.length}${results.length === 120 ? '+' : ''} result${results.length === 1 ? '' : 's'}`
            : EMOJI_CATEGORIES.find((c) => c.id === category)?.label}
        </div>
        {shown.length === 0 ? (
          <div className="emoji-grid-empty">No emoji match “{query.trim()}”.</div>
        ) : (
          <div className="emoji-grid-items">
            {shown.map((e, i) => (
              <button
                // Codepoints repeat across skin-tone variants, so the char alone
                // is not a stable key within a category.
                key={`${e.char}-${i}`}
                type="button"
                className="emoji-grid-item"
                title={e.names[e.names.length - 1] || e.char}
                aria-label={e.names[e.names.length - 1] || e.char}
                onClick={() => onSelect?.(e.char)}
              >
                {e.char}
              </button>
            ))}
          </div>
        )}
      </OsScroll>
    </div>
  );
}
